//Libraries
import moment, { Moment } from "moment";
import { QueryClient } from "@tanstack/react-query";

// Constants
import { ID_NEW_SETUP } from "@Motivators/constants";

//Types
import {
	NewGenericItem,
	TNewItem,
	TAttribute,
	TMotivatorType,
	TParamsSetup,
	SpiffItem,
	OpportunityMiningItem,
	DefaultItem,
	MultipleDataTree,
	CommonFields,
	TemporaryGuaranteedItem,
	SpecialUnitsListItem,
	IHandleUpdateTitle,
	IHandleUpdateEffectiveDateTime,
	IHandleCreate,
	IInputParams,
} from "@Motivators/types";

export function createNewItem({ tree = [], ccId, newItem, param }: NewGenericItem) {
	return tree.map((item) => {
		const arr = item[param]?.filter((child) => (child as CommonFields)._id !== ID_NEW_SETUP) ?? [];

		if (item._id === ccId) {
			return {
				...item,
				[param]: [...arr, newItem],
			};
		}

		return { ...item, [param]: arr };
	});
}

export function newObjectSetup(
	title: string,
	motivatorType: TMotivatorType,
	commissionCycleId: string,
	commissionCycleStartDate: string,
	commissionCycleEndDate: string
): TNewItem | null {
	const obj: DefaultItem = {
		_id: ID_NEW_SETUP,
		effectiveDateFrom: null,
		effectiveDateTo: null,
		saving: false,
		title,
		commissionCycleId,
		commissionCycleStartDate,
		commissionCycleEndDate,
	};

	switch (motivatorType) {
		case "spiffSetup":
		case "spiffBonusSetup":
			return obj;
		case "autoAlertsSetup":
			return {
				autoAlertsSetupId: obj,
			};
		case "salesTemporaryGuaranteed":
		case "temporaryGuaranteed":
			return {
				temporaryGuaranteedId: obj,
			};
		case "specialUnitsSetup":
			return {
				specialUnitsListId: obj,
			};
		default:
			return null;
	}
}

export function copyObjectSetup(motivatorType: TMotivatorType, actualSetup: DefaultItem): TNewItem | null {
	const obj = { ...actualSetup, _id: ID_NEW_SETUP };

	switch (motivatorType) {
		case "spiffSetup":
		case "spiffBonusSetup":
			return obj;
		case "autoAlertsSetup":
			return {
				autoAlertsSetupId: obj,
			};
		case "temporaryGuaranteed":
			return {
				temporaryGuaranteedId: obj,
			};
		case "specialUnitsSetup":
			return {
				specialUnitsListId: obj,
			};
		default:
			return null;
	}
}

export function unMaskId(id: string | undefined) {
	return id?.includes("_") ? id?.split("_")[1] : id;
}

export function isNewItem(id: string) {
	return unMaskId(id) === ID_NEW_SETUP;
}

export function areAllValidParams(params: TParamsSetup, defaultTitle?: string) {
	const allParamsWithValues = Object.values(params).every(
		(value) => (typeof value == "object" && value !== null) || (typeof value === "string" && value.length > 0)
	);

	return allParamsWithValues && !isDefaultTitle(params.title, defaultTitle);
}

export function isDefaultTitle(title: string, defaultTitle?: string) {
	return title.toLocaleLowerCase() === defaultTitle?.toLocaleLowerCase();
}

export function removeUnsaveItemInTree(
	commissionCycleId: string,
	dataTree: MultipleDataTree[],
	param: TAttribute,
	motivatorType: TMotivatorType
): MultipleDataTree[] {
	return dataTree
		? dataTree?.map((item: MultipleDataTree) => {
				if (item._id === commissionCycleId) {
					return {
						...item,
						[param]: (Array.isArray(item[param]) ? item[param] : []).filter(
							(child) =>
								validateSpiffFilter(motivatorType, child) ||
								validateAutoAlertFilter(motivatorType, child) ||
								validateTemporaryGuaranteedFilter(motivatorType, child) ||
								validateSpecialUnitsFilter(motivatorType, child)
						),
					};
				}
				return item;
		  })
		: [];
}

function validateSpiffFilter(motivatorType: TMotivatorType, child: TNewItem) {
	return (motivatorType === "spiffSetup" || motivatorType === "spiffBonusSetup") && (child as SpiffItem)._id !== ID_NEW_SETUP;
}

function validateAutoAlertFilter(motivatorType: TMotivatorType, child: TNewItem) {
	return motivatorType === "autoAlertsSetup" && (child as OpportunityMiningItem).autoAlertsSetupId._id !== ID_NEW_SETUP;
}

function validateTemporaryGuaranteedFilter(motivatorType: TMotivatorType, child: TNewItem) {
	return motivatorType === "temporaryGuaranteed" && (child as TemporaryGuaranteedItem).temporaryGuaranteedId._id !== ID_NEW_SETUP;
}

function validateSpecialUnitsFilter(motivatorType: TMotivatorType, child: TNewItem) {
	return motivatorType === "specialUnitsSetup" && (child as SpecialUnitsListItem).specialUnitsListId._id !== ID_NEW_SETUP;
}

export function determineDate(date: string | Moment | null) {
	if (typeof date === "string") {
		return moment(date);
	}
	return date ?? null;
}

export function canAddItems(hasEditPermission: boolean, dateRange: TParamsSetup, id?: string) {
	return hasEditPermission && Boolean(dateRange.from) && Boolean(dateRange.to) && id !== ID_NEW_SETUP;
}

export function getSetupByMotivatorType(motivatorType: TMotivatorType, newSetup: TNewItem) {
	let newSetupType = null;
	switch (motivatorType) {
		case "spiffSetup":
		case "spiffBonusSetup":
			newSetupType = newSetup as SpiffItem;
			break;
		case "autoAlertsSetup":
			newSetupType = (newSetup as OpportunityMiningItem)?.autoAlertsSetupId;
			break;
		case "salesTemporaryGuaranteed":
		case "temporaryGuaranteed":
			newSetupType = (newSetup as TemporaryGuaranteedItem)?.temporaryGuaranteedId;
			break;
		case "specialUnitsSetup":
			newSetupType = (newSetup as SpecialUnitsListItem)?.specialUnitsListId;
			break;
	}
	return newSetupType;
}

export function onMutateTitle(queryClient: QueryClient, key: string[], title: string) {
	const previousData = queryClient.getQueryData(key);
	queryClient.setQueryData(key, (old: MultipleDataTree) => {
		return {
			...old,
			title,
		};
	});
	return { previousData, title };
}

export function defineTypeEffetiveDate(type: "from" | "to") {
	return type === "from" ? "effectiveDateFrom" : "effectiveDateTo";
}

export function handleCreateSetup({ titleDefault, commissionCycleId, paramsAction: newParams, createAction: createSetup }: IHandleCreate) {
	if (areAllValidParams(newParams, titleDefault)) {
		const newSetup: IInputParams = {
			input: {
				title: newParams.title,
				effectiveDateFrom: newParams.from,
				effectiveDateTo: newParams.to,
			},
			commissionCycleId,
		};
		createSetup(newSetup);
	}
}

export function handleUpdateTitle({
	title,
	titleDefault,
	paramsAction,
	selectedItemId,
	commissionCycleId,
	setParamsHeader,
	setLoading,
	mutateFunction,
	createAction,
}: IHandleUpdateTitle) {
	const newParams: TParamsSetup = { ...paramsAction, title };
	setParamsHeader(newParams);
	if (!isNewItem(selectedItemId) && !isDefaultTitle(newParams.title, titleDefault)) {
		setLoading(true);
		mutateFunction(title);
	} else {
		handleCreateSetup({ paramsAction: newParams, titleDefault, commissionCycleId, createAction });
	}
}

export async function handleUpdateEffectiveDateTime({
	dateValue,
	type,
	titleDefault,
	paramsAction: paramsHeader,
	selectedItemId,
	commissionCycleId,
	setParamsHeader,
	setLoading: setLoadingDateRange,
	mutateFunction: mutateDate,
	createAction: createSetup,
}: IHandleUpdateEffectiveDateTime) {
	const newParams = { ...paramsHeader, [type]: dateValue };
	setParamsHeader(newParams);
	if (!isNewItem(selectedItemId) && !isDefaultTitle(newParams.title, titleDefault)) {
		setLoadingDateRange((loadingRange) => ({ ...loadingRange, [type]: true }));
		const input = { dateValue, type };
		mutateDate(input);
	} else {
		handleCreateSetup({ titleDefault, commissionCycleId, paramsAction: newParams, createAction: createSetup });
	}
}

export function canUserEditMotivators(permission = false, statusCommissionCycle = "") {
	const allowedStatusForCommissionCycles = ["ACTIVE", "CLOSING"];

	return allowedStatusForCommissionCycles.includes(statusCommissionCycle.toUpperCase()) && permission;
}
